import {omit, startCase} from "lodash"
import {Result} from "neverthrow"
import {v4 as uuidv4} from "uuid"
import {
	Address,
	createResource,
	deleteResource,
	ErrorDocument,
	getFile,
	getResource,
	OkDocument,
	Phone,
	Resource,
	updateResource,
} from "./common"
import {Cents} from "./transaction"

export const BillListInclude = "customer,vendor,org"
export const BillDetailInclude = "customer,vendor,org,billFile"
export const BillEditInclude = "vendor"
export const VendorDetailInclude = "customer,org"

export const VendorResourceTypes = ["vendor", "billPayVendor"]

export type VendorPaymentMethods = {
	ach?: {
		name?: string
		routingNumber?: string
		accountNumber?: string
	}
	check?: {
		name?: string
		address?: Address
	}
}

export type Vendor = {
	type: "vendor" | "billPayVendor"
	id: string
	attributes: {
		legalName?: string
		email?: string
		website?: string
		logoUrl?: string
		phone?: Phone
		address?: Address
		paymentMethods?: VendorPaymentMethods
		tags?: {[key: string]: string}
	}
	relationships?: {
		customer?: {
			data: {
				type: string
				id: string
			}
		}
		org?: {
			data: {
				type: string
				id: string
			}
		}
	}
}

export function getVendorName(vendor: Vendor): string {
	return vendor.attributes?.legalName || vendor.id
}

export function findVendor(vendorId: string, include?: Resource[]): Vendor | undefined {
	return include?.find((r) => VendorResourceTypes.includes(r.type) && r.id === vendorId.toString()) as
		| Vendor
		| undefined
}

export type BillFile = {
	type: "billFile"
	id: string
	attributes: {
		link: string
		contentType: string
		fieldsExtractionStatus?: string
		vendorMatchingStatus?: string
		createdAt?: string
		updatedAt?: string
	}
}

export function isBillFileImage(billFile: BillFile): boolean {
	return billFile.attributes.contentType.startsWith("image/")
}

export function isBillFilePdf(billFile: BillFile): boolean {
	return billFile.attributes.contentType === "application/pdf"
}

export enum BillStatus {
	Draft = "Draft",
	Pending = "Pending",
	Scheduled = "Scheduled",
	Paid = "Paid",
	PaymentInProgress = "PaymentInProgress",
	FundsSettled = "FundsSettled",
	FundsPushed = "FundsPushed",
	DeductionPaymentFailed = "DeductionPaymentFailed",
	VendorPaymentFailed = "VendorPaymentFailed",
	FundsDeductionBlocked = "FundsDeductionBlocked",
	RefundInitiated = "RefundInitiated",
	Refunded = "Refunded",
	CancellationInitiated = "CancellationInitiated",
	Canceled = "Canceled",
	Archived = "Archived",
}

export const BillEditableStatuses: BillStatus[] = [
	BillStatus.Draft,
	BillStatus.DeductionPaymentFailed,
	BillStatus.VendorPaymentFailed,
	BillStatus.FundsDeductionBlocked,
]

export function isBillEditableStatus(status: BillStatus) {
	return BillEditableStatuses.includes(status)
}

export const BillRepayableStatuses: BillStatus[] = [
	BillStatus.Canceled,
	BillStatus.VendorPaymentFailed,
	BillStatus.FundsDeductionBlocked,
]

export function isBillRepayableStatus(status: BillStatus) {
	return BillRepayableStatuses.includes(status)
}

export function canArchiveBill(status: BillStatus) {
	return status === BillStatus.Draft || status === BillStatus.Scheduled
}

export enum BillRefundableFailureReason {
	AccountClosed = "AccountClosed",
	NoAccount = "NoAccount",
	InvalidAccountNumberStructure = "InvalidAccountNumberStructure",
	RepresentativePayeeDeceasedOrUnableToContinue = "RepresentativePayeeDeceasedOrUnableToContinue",
	BeneficiaryOrBankAccountHolderDeceased = "BeneficiaryOrBankAccountHolderDeceased",
}

const BillRefundableFailureReasons: string[] = Object.values(BillRefundableFailureReason)

const BillRefundableFailedStatuses: BillStatus[] = [
	BillStatus.DeductionPaymentFailed,
	BillStatus.VendorPaymentFailed,
	BillStatus.FundsDeductionBlocked,
]

const BillRefundableCheckStatuses: BillStatus[] = [
	BillStatus.VendorPaymentFailed,
	BillStatus.FundsDeductionBlocked,
	BillStatus.Canceled,
]

export enum BillPaymentMethod {
	Ach = "ach",
	SameDayAch = "sameDayAch",
	Check = "check",
	External = "external",
}

export type PlatformPaymentMethod = {
	deductionAccount?: string
	deductionDate?: string
	expectedDate?: string
}

export type ExternalPaymentMethod = {
	paymentDate?: string
}

export type BillPaymentMethodPayload =
	| {[BillPaymentMethod.Ach]: PlatformPaymentMethod}
	| {[BillPaymentMethod.SameDayAch]: PlatformPaymentMethod}
	| {[BillPaymentMethod.Check]: PlatformPaymentMethod}
	| {[BillPaymentMethod.External]: ExternalPaymentMethod}

export function getBillDeductionDate(paymentMethod?: BillPaymentMethodPayload): string | undefined {
	if (!paymentMethod) {
		return undefined
	}

	if (BillPaymentMethod.Ach in paymentMethod) {
		return paymentMethod.ach.deductionDate
	}
	if (BillPaymentMethod.SameDayAch in paymentMethod) {
		return paymentMethod.sameDayAch.deductionDate
	}
	if (BillPaymentMethod.Check in paymentMethod) {
		return paymentMethod.check.deductionDate
	}

	return undefined
}

export type BillLineItem = {
	totalAmount: Cents
	description: string
	tax: Cents
	quantity: number
	unitPrice: Cents
}

export type BillEventPayload = Record<string, unknown>

export type BillEvent = {
	id?: string
	type: string
	attributes: BillEventPayload & {
		version?: number
		createdAt?: string
	}
	relationships?: {
		bill?: {
			data: {
				type: string
				id: string
			}
		}
		vendor?: {
			data: {
				type: string
				id: string
			}
		}
	}
}

export function getBillEventId(event: BillEvent): string {
	if (event.id) {
		return event.id
	}
	const version = event.attributes.version
	return version != null ? String(version) : "--"
}

export function getBillEventType(event: BillEvent): string {
	return event.type
}

export function getBillEventTypeDisplay(event: BillEvent): string {
	return startCase(getBillEventType(event))
}

export function getBillEventPayload(event: BillEvent): BillEventPayload {
	return omit(event.attributes, ["version", "createdAt"])
}

export function getBillEventKey(event: BillEvent, index: number): string {
	return `${event.type}-${event.attributes.version ?? index}-${event.attributes.createdAt ?? index}`
}

export function getPayloadField(payload: BillEventPayload | undefined, field: string): string | undefined {
	if (!payload) {
		return undefined
	}
	const value = payload[field]
	if (value == null || value === "") {
		return undefined
	}
	return String(value)
}

export function getBillEventLinkFields(event: BillEvent) {
	const payload = getBillEventPayload(event)
	return {
		accountId: getPayloadField(payload, "accountId"),
		paymentId: getPayloadField(payload, "paymentId"),
		transactionId: getPayloadField(payload, "transactionId"),
		paymentEntityId: getPayloadField(payload, "paymentEntityId"),
	}
}

export const BillDeductionStartedEventType = "billDeductionStarted"
export const BillFundsPushedToVendorEventType = "billFundsPushedToVendor"

export function findBillEventPaymentId(events: BillEvent[], eventType: string): string | undefined {
	const event = events.find((e) => e.type === eventType)
	return event ? getBillEventLinkFields(event).paymentId : undefined
}

export function findBillEventPaymentEntityId(events: BillEvent[], eventType: string): string | undefined {
	const event = events.find((e) => e.type === eventType)
	return event ? getBillEventLinkFields(event).paymentEntityId : undefined
}

export function getBillVendorPaymentPath(
	paymentMethod: BillPaymentMethodPayload | undefined,
	paymentId: string
): string {
	if (paymentMethod && BillPaymentMethod.Check in paymentMethod) {
		return `/check-payments/${paymentId}`
	}

	return `/payments/${paymentId}`
}

export type Bill = {
	type: "bill"
	id: string
	attributes: {
		invoiceNumber: string
		total: Cents
		dueDate: string
		date: string
		tax: Cents
		tags?: {[key: string]: string}
		status: BillStatus
		failureReason?: string
		lineItems?: BillLineItem[]
		paymentMethod?: BillPaymentMethodPayload
		createdAt?: string
		updatedAt?: string
		paymentDate?: string
	}
	relationships: {
		customer: {
			data: {
				type: string
				id: string
			}
		}
		vendor: {
			data: {
				type: string
				id: string
			}
		}
		org?: {
			data: {
				type: string
				id: string
			}
		}
		billFile?: {
			data: {
				type: "billFile"
				id: string
			}
		}
		deductionAccount?: {
			data: {
				type: string
				id: string
			}
		}
	}
}

export const CHECK_CANCEL_DELAY_DAYS = 15
const millisecondsPerDay = 1000 * 3600 * 24

function dateOnly(date: string | Date) {
	return new Date(new Date(date).toDateString())
}

function daysBetween(from: string | Date, to: string | Date): number {
	const timeDifference = new Date(to).getTime() - new Date(from).getTime()
	return Math.round(timeDifference / millisecondsPerDay)
}

export function canCancelCheck(bill: Bill): boolean {
	const paymentMethod = bill.attributes.paymentMethod
	if (!paymentMethod || !(BillPaymentMethod.Check in paymentMethod)) {
		return false
	}

	const deductionDate = paymentMethod.check.deductionDate
	if (!deductionDate) {
		return false
	}

	const days = daysBetween(dateOnly(deductionDate), dateOnly(new Date()))
	return bill.attributes.status === BillStatus.FundsPushed && days > CHECK_CANCEL_DELAY_DAYS
}

export function findBillFile(bill: Bill, included?: ReadonlyArray<{type: string; id: string}>): BillFile | undefined {
	const billFileId = bill.relationships.billFile?.data.id
	if (!billFileId || !included) {
		return undefined
	}

	return included.find((resource) => resource.type === "billFile" && resource.id === billFileId) as BillFile | undefined
}

export async function findBills(
	accessToken: string,
	offset: number,
	sort: string,
	searchQuery: string,
	limit?: number | null,
	statusFilter?: BillStatus[],
	orgs?: string[],
	since?: string,
	until?: string,
	customerId?: string,
	vendorIds?: string[]
) {
	const orgFilter = orgs && orgs.length > 0 ? orgs : undefined
	const query = {
		page: {
			limit: limit,
			offset,
		},
		filter: {
			statuses: statusFilter,
			query: searchQuery != "" ? searchQuery : null,
			orgs: orgFilter,
			since: since != "" ? since : null,
			until: until != "" ? until : null,
			...(customerId && {customerId}),
			...(vendorIds && vendorIds.length > 0 && {vendorIds}),
		},
		sort: sort,
		include: BillListInclude,
	}

	return await getResource<Array<Bill>>("billpay/bills", accessToken, query)
}

export async function getBill(accessToken: string, id: string, include?: string) {
	return await getResource<Bill>(`billpay/bills/${id}`, accessToken, {include})
}

export async function updateBill(
	accessToken: string,
	id: string,
	paymentMethod: BillPaymentMethodPayload,
	customerId: string
) {
	return await updateResource<Bill>(`billpay/bills/${id}`, accessToken, {
		type: "bill",
		attributes: {
			idempotencyKey: uuidv4(),
			paymentMethod,
		},
		relationships: {
			customer: {
				data: {
					type: "customer",
					id: String(customerId),
				},
			},
		},
	})
}

export async function repayBill(accessToken: string, id: string) {
	return await createResource<Bill>(`billpay/bills/${id}/repay`, accessToken, {})
}

export async function archiveBill(accessToken: string, id: string) {
	return await deleteResource<Bill>(`billpay/bills/${id}`, accessToken)
}

export async function cancelBillCheck(accessToken: string, id: string) {
	return await createResource<Bill>(`billpay/bills/${id}/cancel`, accessToken, {})
}

export function getBillPaymentMethod(bill: Bill): BillPaymentMethod | undefined {
	const paymentMethod = bill.attributes.paymentMethod
	if (!paymentMethod) {
		return undefined
	}
	if (BillPaymentMethod.Ach in paymentMethod) {
		return BillPaymentMethod.Ach
	}
	if (BillPaymentMethod.SameDayAch in paymentMethod) {
		return BillPaymentMethod.SameDayAch
	}
	if (BillPaymentMethod.Check in paymentMethod) {
		return BillPaymentMethod.Check
	}
	return BillPaymentMethod.External
}

const billPaymentMethodLabels: Record<string, string> = {
	[BillPaymentMethod.Ach]: "ACH",
	[BillPaymentMethod.SameDayAch]: "Same Day ACH",
	[BillPaymentMethod.Check]: "Check",
	[BillPaymentMethod.External]: "External (marked as paid)",
}

// Returns undefined when the bill has no payment method, or when it is one we don't have a label for.
export function getBillPaymentMethodLabel(bill: Bill): string | undefined {
	const paymentMethod = bill.attributes.paymentMethod
	const method = paymentMethod ? Object.keys(paymentMethod)[0] : undefined
	return method ? billPaymentMethodLabels[method] : undefined
}

export function getBillDeductionAccountId(bill: Bill): string | undefined {
	const fromRelationship = bill.relationships.deductionAccount?.data.id
	if (fromRelationship) {
		return String(fromRelationship)
	}

	const paymentMethod = bill.attributes.paymentMethod
	if (!paymentMethod) {
		return undefined
	}
	if (BillPaymentMethod.Ach in paymentMethod) {
		return paymentMethod.ach.deductionAccount
	}
	if (BillPaymentMethod.SameDayAch in paymentMethod) {
		return paymentMethod.sameDayAch.deductionAccount
	}
	if (BillPaymentMethod.Check in paymentMethod) {
		return paymentMethod.check.deductionAccount
	}
	return undefined
}

export function isBillRefundable(bill: Bill): boolean {
	const status = bill.attributes.status
	const paymentMethod = getBillPaymentMethod(bill)
	const failureReason = bill.attributes.failureReason
	const isWrongAccountDetails = Boolean(failureReason && BillRefundableFailureReasons.includes(failureReason))

	if (status === BillStatus.FundsDeductionBlocked) {
		return true
	}
	if (paymentMethod === BillPaymentMethod.Check && BillRefundableCheckStatuses.includes(status)) {
		return true
	}
	if (BillRefundableFailedStatuses.includes(status) && isWrongAccountDetails) {
		return true
	}
	return false
}

export async function refundBill(accessToken: string, id: string) {
	return await createResource(`billpay/bills/refund`, accessToken, {
		type: "refundBills",
		relationships: {
			bills: {
				data: [{id, type: "bill"}],
			},
		},
	})
}

export async function getVendor(accessToken: string, id: string, include?: string) {
	return await getResource<Vendor>(`billpay/vendors/${id}`, accessToken, {include})
}

export async function getBillEvents(accessToken: string, billId: string) {
	return await getResource<Array<BillEvent>>(`billpay/bills/${billId}/events`, accessToken)
}

export async function exportBills(
	accessToken: string,
	statusFilter: BillStatus[],
	sort: string,
	orgs?: string[],
	since?: string,
	until?: string,
	customerId?: string,
	vendorIds?: string[]
): Promise<Result<OkDocument<Array<Bill>>, ErrorDocument>> {
	const statusesFilter = statusFilter.length > 0 ? statusFilter : null
	const orgFilter = orgs && orgs.length > 0 ? orgs : null

	const query = {
		page: {
			limit: 10000,
			offset: 0,
		},
		filter: {
			statuses: statusesFilter,
			orgs: orgFilter,
			since: since !== "" ? since : null,
			until: until !== "" ? until : null,
			...(customerId && {customerId}),
			...(vendorIds && vendorIds.length > 0 && {vendorIds}),
		},
		sort,
		include: BillListInclude,
	}

	return await getFile("billpay/bills", accessToken, query)
}
